/* Lorena Canals: shared behaviour for lc-* product cards on any page
   (homepage carousel, hero card, collection grid):
   tabs, colour swatches via the Linkify app, in-place colour switching,
   card alignment, quick-add popup with shipping status. Loaded by snippets/lc-head.liquid. */
(() => {
if (!customElements.get('lc-tabs')) {
    customElements.define('lc-tabs', class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.queueSwatches();
        this.querySelectorAll('.lc-showcase__arrow').forEach((btn) => {
          btn.addEventListener('click', () => {
            const panel = this.panels.find((p) => !p.hidden);
            if (!panel) return;
            panel.scrollBy({ left: Number(btn.dataset.dir) * panel.clientWidth * 0.8, behavior: 'smooth' });
          });
        });
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            e.preventDefault();
            const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          });
        });
      }
      queueSwatches() {
        // Ask the Linkify Product Colors app to render swatches for our cards
        const anchors = this.querySelectorAll('webyze-product-colors-anchor[data-product]:not([data-lc-queued])');
        if (!anchors.length) return;
        window.webyzeProductColorsQueue = window.webyzeProductColorsQueue || [];
        anchors.forEach((anchor) => {
          const productId = Number(anchor.dataset.product);
          if (!Number.isSafeInteger(productId)) return;
          anchor.setAttribute('data-lc-queued', '');
          window.webyzeProductColorsQueue.push({ productId, shopDomain: anchor.dataset.shopDomain || (window.Shopify && window.Shopify.shop) });
        });
      }
      copySwatches() {
        // The app renders each product once; copy swatches to the same product in other tabs
        this.querySelectorAll('[data-lc-swatch-product]').forEach((box) => {
          if (!box.querySelector('webyze-product-colors-anchor')) return;
          const done = Array.from(this.querySelectorAll('[data-lc-swatch-product="' + box.dataset.lcSwatchProduct + '"]'))
            .find((b) => b !== box && !b.querySelector('webyze-product-colors-anchor') && b.children.length);
          if (done) box.innerHTML = done.innerHTML;
        });
      }
      select(index) {
        this.tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
          tab.tabIndex = i === index ? 0 : -1;
        });
        this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
        this.copySwatches();
      }
    });
  }

  /* ---------- Colour swatches outside the carousel (e.g. the hero card) ---------- */
  const lcQueueAnchors = () => {
    const anchors = document.querySelectorAll('[data-lc-swatch-product] webyze-product-colors-anchor[data-product]:not([data-lc-queued])');
    if (!anchors.length) return;
    window.webyzeProductColorsQueue = window.webyzeProductColorsQueue || [];
    anchors.forEach((anchor) => {
      const productId = Number(anchor.dataset.product);
      if (!Number.isSafeInteger(productId)) return;
      anchor.setAttribute('data-lc-queued', '');
      window.webyzeProductColorsQueue.push({ productId, shopDomain: anchor.dataset.shopDomain || (window.Shopify && window.Shopify.shop) });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', lcQueueAnchors); else lcQueueAnchors();
  document.addEventListener('shopify:section:load', lcQueueAnchors);


  /* ---------- Card photo gallery: arrows + dots ---------- */
  const lcGalUpdate = (track) => {
    const media = track.closest('.lc-card__media');
    if (!media) return;
    const w = track.clientWidth || 1;
    const count = track.children.length;
    const i = Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / w)));
    media.querySelectorAll('.lc-gal__dots span').forEach((d, n) => d.classList.toggle('is-on', n === i));
    const prev = media.querySelector('.lc-gal__arrow--prev');
    const next = media.querySelector('.lc-gal__arrow--next');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= count - 1;
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-lc-gal-step]');
    if (!btn) return;
    e.preventDefault();
    const track = btn.closest('.lc-card__media')?.querySelector('.lc-gal__track');
    if (track) track.scrollBy({ left: Number(btn.dataset.lcGalStep) * track.clientWidth, behavior: 'smooth' });
  });
  // Photos load in advance (next and previous one), so a swipe never shows an empty frame on a slow connection
  const lcGalWarm = (track, i) => {
    [i + 1, i - 1, i + 2].forEach((n) => {
      const img = track.children[n] && track.children[n].querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    });
  };
  if ('IntersectionObserver' in window) {
    const lcWarmIO = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        lcWarmIO.unobserve(en.target);
        lcGalWarm(en.target, 0);
      });
    }, { rootMargin: '300px 0px' });
    const lcWarmWatch = () => document.querySelectorAll('.lc-gal__track:not([data-lc-warm])').forEach((t) => { t.setAttribute('data-lc-warm', ''); lcWarmIO.observe(t); });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', lcWarmWatch); else lcWarmWatch();
    new MutationObserver(() => { clearTimeout(lcWarmWatch._t); lcWarmWatch._t = setTimeout(lcWarmWatch, 200); }).observe(document.documentElement, { childList: true, subtree: true });
  }
  let lcGalRaf = 0;
  document.addEventListener('scroll', (e) => {
    const track = e.target;
    if (!track.classList || !track.classList.contains('lc-gal__track')) return;
    cancelAnimationFrame(lcGalRaf);
    lcGalRaf = requestAnimationFrame(() => { lcGalUpdate(track); lcGalWarm(track, Math.round(track.scrollLeft / (track.clientWidth || 1))); });
  }, { capture: true, passive: true });
  // a swipe should not open the product page
  document.addEventListener('pointerdown', (e) => {
    const track = e.target.closest && e.target.closest('.lc-gal__track');
    if (track) track._lcStart = track.scrollLeft;
  }, true);
  document.addEventListener('click', (e) => {
    const track = e.target.closest && e.target.closest('.lc-gal__track');
    if (!track) return;
    if (track._lcDragged || (track._lcStart != null && Math.abs(track.scrollLeft - track._lcStart) > 8)) { e.preventDefault(); }
    track._lcDragged = false;
  }, true);
  // desktop: drag the photos with the mouse
  let lcDrag = null;
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const t = e.target.closest && e.target.closest('.lc-gal__track');
    if (!t) return;
    e.preventDefault(); // stops the browser from dragging the link or photo instead
    lcDrag = { t, x: e.clientX, left: t.scrollLeft, moved: false };
  });
  document.addEventListener('pointermove', (e) => {
    if (!lcDrag) return;
    const dx = e.clientX - lcDrag.x;
    if (!lcDrag.moved && Math.abs(dx) < 6) return;
    if (!lcDrag.moved) {
      lcDrag.moved = true;
      lcDrag.t.style.scrollSnapType = 'none';
      lcDrag.t.style.scrollBehavior = 'auto';
      lcDrag.t.classList.add('is-dragging');
    }
    lcDrag.t.scrollLeft = lcDrag.left - dx;
  });
  const lcDragEnd = () => {
    if (!lcDrag) return;
    const { t, moved, left } = lcDrag;
    lcDrag = null;
    if (!moved) return;
    t._lcDragged = true;
    const w = t.clientWidth || 1;
    const from = Math.round(left / w);
    const moveBy = (t.scrollLeft - left) / w;
    // a short drag is enough to go to the next / previous photo
    const to = Math.max(0, Math.min(t.children.length - 1, from + (moveBy > 0.12 ? 1 : moveBy < -0.12 ? -1 : 0)));
    t.classList.remove('is-dragging');
    t.style.scrollBehavior = '';
    t.scrollTo({ left: to * w, behavior: 'smooth' });
    setTimeout(() => { t.style.scrollSnapType = ''; }, 450);
  };
  document.addEventListener('pointerup', lcDragEnd);
  document.addEventListener('pointercancel', lcDragEnd);
  document.addEventListener('dragstart', (e) => { if (e.target.closest && e.target.closest('.lc-gal__track')) e.preventDefault(); });
  window.lcGalUpdate = lcGalUpdate;
  /* ---------- Card hover (desktop): galleries slide to the second photo while the mouse is over the card ---------- */
  if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('mouseover', (e) => {
      const media = e.target.closest && e.target.closest('[class*="lc-cgrid--hover-"] .lc-card:not(.lc-panel) .lc-card__media--gallery');
      if (!media || media._lcHover || (e.relatedTarget && media.contains(e.relatedTarget))) return;
      const track = media.querySelector('.lc-gal__track');
      if (!track || track.scrollLeft > 2 || track.children.length < 2) return;
      media._lcHover = true;
      track.scrollTo({ left: track.clientWidth, behavior: 'smooth' });
    });
    document.addEventListener('mouseout', (e) => {
      const media = e.target.closest && e.target.closest('[class*="lc-cgrid--hover-"] .lc-card:not(.lc-panel) .lc-card__media--gallery');
      if (!media || !media._lcHover || (e.relatedTarget && media.contains(e.relatedTarget))) return;
      media._lcHover = false;
      const track = media.querySelector('.lc-gal__track');
      // only rewind if the shopper did not browse further with the arrows
      if (track && Math.round(track.scrollLeft / (track.clientWidth || 1)) === 1) track.scrollTo({ left: 0, behavior: 'smooth' });
    });
  }

  /* ---------- Galleries go back to the first photo once you move on ---------- */
  const lcGalReset = (track, smooth) => {
    if (!track || track.scrollLeft < 2) return;
    track.style.scrollSnapType = 'none';
    track.scrollTo({ left: 0, behavior: smooth ? 'smooth' : 'auto' });
    setTimeout(() => { track.style.scrollSnapType = ''; lcGalUpdate(track); }, smooth ? 450 : 30);
  };
  // 1. when you start browsing another product, the previous one rewinds
  let lcGalActive = null;
  const lcGalTouch = (track) => {
    if (!track || track === lcGalActive) return;
    if (lcGalActive && document.contains(lcGalActive)) lcGalReset(lcGalActive, true);
    lcGalActive = track;
  };
  document.addEventListener('pointerdown', (e) => {
    const t = e.target.closest && e.target.closest('.lc-gal__track');
    if (t) lcGalTouch(t);
  }, true);
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-lc-gal-step]');
    if (btn) lcGalTouch(btn.closest('.lc-card__media')?.querySelector('.lc-gal__track'));
  }, true);
  // 2. when a product scrolls out of view, it is back on its first photo next time you see it
  if ('IntersectionObserver' in window) {
    const lcGalIO = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) {
          lcGalReset(en.target, false);
          if (en.target === lcGalActive) lcGalActive = null;
        }
      });
    });
    const lcGalWatch = () => document.querySelectorAll('.lc-gal__track:not([data-lc-io])').forEach((t) => { t.setAttribute('data-lc-io', ''); lcGalIO.observe(t); });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', lcGalWatch); else lcGalWatch();
    new MutationObserver(() => { clearTimeout(lcGalWatch._t); lcGalWatch._t = setTimeout(lcGalWatch, 150); }).observe(document.documentElement, { childList: true, subtree: true });
  }


  /* ---------- Hero "curtain": keep the banner under the sticky menu and dim it as it gets covered ---------- */
  const lcCurtain = () => {
    const heroes = document.querySelectorAll('.lc-hero--curtain');
    if (!heroes.length) return;
    let raf = 0;
    const headerBottom = () => {
      const header = document.querySelector('#header-group, .header-section, header-component, .header');
      if (!header) return 0;
      const els = [header, header.querySelector('[data-sticky-state], .header__row, header')].filter(Boolean);
      for (const el of els) {
        const pos = getComputedStyle(el).position;
        const r = el.getBoundingClientRect();
        if ((pos === 'sticky' || pos === 'fixed') && r.bottom > 0) return Math.round(r.bottom);
      }
      return 0;
    };
    const update = () => {
      raf = 0;
      const top = headerBottom();
      heroes.forEach((hero) => {
        const wrap = hero.parentElement;
        if (!wrap) return;
        wrap.style.setProperty('--lc-hero-top', top + 'px');
        const next = wrap.nextElementSibling;
        const h = hero.offsetHeight || 1;
        let cover = 0;
        if (next) cover = 1 - (next.getBoundingClientRect().top - top) / h;
        hero.style.setProperty('--lc-cover', Math.max(0, Math.min(1, cover)).toFixed(3));
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', lcCurtain); else lcCurtain();
  document.addEventListener('shopify:section:load', lcCurtain);

  /* ---------- Hero "under the menu": pull the banner behind the header, clear menu until you scroll past it ---------- */
  const lcUnder = () => {
    const hero = document.querySelector('.lc-hero--under');
    const root = document.documentElement;
    if (!hero) { root.classList.remove('lc-menu-over'); return; }
    const wrap = hero.parentElement;
    const group = document.querySelector('#header-group') || document.querySelector('.header-section, header-component, .header');
    // the announcement bar keeps its colour: only the menu row goes clear
    const menu = document.querySelector('header-component, .header-section .header, #header-group .header') || group;
    const update = () => {
      if (!menu) return;
      const h = Math.round(menu.offsetHeight || 0);
      wrap.style.setProperty('--lc-under', h + 'px');
      hero.style.setProperty('--lc-under', h + 'px');
      const heroBottom = hero.getBoundingClientRect().bottom;
      const menuBottom = menu.getBoundingClientRect().bottom;
      root.classList.toggle('lc-menu-over', heroBottom > menuBottom + 8 && !document.body.classList.contains('menu-open'));
    };
    const onScroll = () => {
      if (window.__lcUnderRaf) return;
      window.__lcUnderRaf = requestAnimationFrame(() => { window.__lcUnderRaf = 0; if (window.__lcUnderUpdate) window.__lcUnderUpdate(); });
    };
    if (!window.__lcUnderBound) {
      window.__lcUnderBound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }
    window.__lcUnderUpdate = update;
    update();
    setTimeout(update, 300);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', lcUnder); else lcUnder();
  window.addEventListener('load', () => window.__lcUnderUpdate && window.__lcUnderUpdate());
  document.addEventListener('shopify:section:load', lcUnder);

  /* ---------- Align cards: names in a row share the tallest name's height ---------- */
  const lcEqualizeTitles = () => {
    document.querySelectorAll('.lc-showcase__grid, .lc-cgrid__grid').forEach((grid) => {
      if (grid.hidden) return;
      const titles = Array.from(grid.querySelectorAll('.lc-card__title'));
      titles.forEach((t) => { t.style.minHeight = ''; });
      // group cards that sit on the same visual row
      const rows = new Map();
      titles.forEach((t) => {
        const card = t.closest('.lc-card');
        const key = Math.round(card ? card.getBoundingClientRect().top + window.scrollY : 0);
        if (!rows.has(key)) rows.set(key, []);
        rows.get(key).push(t);
      });
      rows.forEach((row) => {
        const max = Math.max(0, ...row.map((t) => t.getBoundingClientRect().height));
        if (max) row.forEach((t) => { t.style.minHeight = max + 'px'; });
      });
    });
  };
  window.addEventListener('resize', lcEqualizeTitles);
  window.addEventListener('load', lcEqualizeTitles);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(lcEqualizeTitles);
  if (document.readyState !== 'loading') lcEqualizeTitles(); else document.addEventListener('DOMContentLoaded', lcEqualizeTitles);
  document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest('[role="tab"], linkify-product-colors-swatch')) setTimeout(lcEqualizeTitles, 400); });
  document.addEventListener('shopify:section:load', lcEqualizeTitles);

  /* ---------- Swatch rows: fade on the right while more colours are hidden ---------- */
  const lcSwatchFades = () => {
    document.querySelectorAll('.lc-card .lc-swatches').forEach((box) => {
      const row = box.querySelector('linkify-product-colors-swatches');
      if (!row) return;
      const overflow = row.scrollWidth > row.clientWidth + 2;
      box.classList.toggle('is-overflow', overflow);
      box.classList.toggle('is-end', !overflow || row.scrollLeft + row.clientWidth >= row.scrollWidth - 2);
    });
  };
  document.addEventListener('scroll', (e) => {
    if (e.target && e.target.matches && e.target.matches('.lc-card .lc-swatches linkify-product-colors-swatches')) lcSwatchFades();
  }, true);
  window.addEventListener('resize', lcSwatchFades);
  let lcFadeTimer;
  new MutationObserver(() => { clearTimeout(lcFadeTimer); lcFadeTimer = setTimeout(() => { lcQueueAnchors(); lcSwatchFades(); lcEqualizeTitles(); }, 150); })
    .observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest('[role="tab"]')) setTimeout(lcSwatchFades, 50); });

  /* ---------- Quick add popup ---------- */
  if (!window.lcQuickAdd) {
    window.lcQuickAdd = true;

    // Styles for the quick-add photo gallery (kept here so only this file needs updating)
    if (!document.getElementById('lc-qa-gallery-css-4')) {
      const st = document.createElement('style');
      st.id = 'lc-qa-gallery-css-4';
      st.textContent = [
        '.lc-qa__media{position:relative;overflow:hidden;min-width:0}',
        '.lc-qa__track{display:flex;height:100%;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none;overscroll-behavior-x:contain}',
        '.lc-qa__track::-webkit-scrollbar{display:none}',
        '.lc-qa__slide{flex:0 0 100%;min-width:0;position:relative;overflow:hidden;scroll-snap-align:start;scroll-snap-stop:always;aspect-ratio:2/3;max-height:calc(100svh - 32px);display:flex}',
        '.lc-qa .lc-qa__slide .lc-qa__img{width:100%;height:100%;aspect-ratio:auto;object-fit:cover;mix-blend-mode:normal;max-height:none}',
        '.lc-qa__arrow{position:absolute;top:50%;transform:translateY(-50%);width:40px;height:40px;border:0;border-radius:50%;background:rgba(252,251,249,.92);color:#392c22;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 2px 10px rgba(57,44,34,.15);transition:opacity .2s}',
        '.lc-qa__arrow--prev{left:12px}.lc-qa__arrow--next{right:12px}',
        '.lc-qa__arrow:disabled{opacity:0;pointer-events:none}',
        '.lc-qa__dots{position:absolute;left:0;right:0;bottom:14px;display:flex;justify-content:center;gap:6px}',
        '.lc-qa__dots button{width:7px;height:7px;padding:0;border:0;border-radius:50%;background:rgba(252,251,249,.6);box-shadow:0 0 0 1px rgba(57,44,34,.25);cursor:pointer}',
        '.lc-qa__dots button.is-on{background:#392c22;box-shadow:none}',
        '.lc-qa__count{position:absolute;left:14px;top:14px;font-size:11px;letter-spacing:.12em;background:rgba(252,251,249,.9);padding:4px 8px;color:#392c22}',
        '.lc-qa .lc-qa__slide:first-child .lc-qa__img{object-fit:contain;mix-blend-mode:multiply;transform:scale(1.12)}',
        '@media (max-width:749px){.lc-qa[open]{display:flex;flex-direction:column}.lc-qa__media{flex:0 0 auto;height:42vh;height:42svh;max-height:none}.lc-qa__body{padding-bottom:0}.lc-qa__row{position:sticky;bottom:0;z-index:3;margin:6px -16px 0;padding:10px 16px calc(10px + env(safe-area-inset-bottom));background:var(--lc-grey-100,#fcfbf9);box-shadow:0 -10px 16px -14px rgba(57,44,34,.35)}.lc-qa__details{margin-bottom:20px}.lc-qa__track{height:100%}.lc-qa__slide{aspect-ratio:auto;height:100%;max-height:none}.lc-qa .lc-qa__slide .lc-qa__img{object-position:center center}.lc-qa__body{flex:0 0 auto}.lc-qa__arrow{width:34px;height:34px}}'
      ].join('');
      document.head.appendChild(st);
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const locale = document.documentElement.lang || 'en';
    const money = (cents) => {
      try { return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100); }
      catch (e) { return (cents / 100).toFixed(2); }
    };
    const shipCache = {};
    const getShip = (handle) => (shipCache[handle] = shipCache[handle] || fetch(root + 'products/' + handle + '?view=lc-ship')
      .then((r) => (r.ok ? r.text() : '{}'))
      .then((txt) => { try { return JSON.parse(txt); } catch (e) { return {}; } })
      .catch(() => ({})));
    const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
    const titleCase = (t) => String(t).toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

    let dialog;
    const getDialog = () => {
      if (dialog) return dialog;
      dialog = el('dialog', 'lc-qa lc-tone--ivory');
      dialog.setAttribute('aria-labelledby', 'lc-qa-title');
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
      document.body.appendChild(dialog);
      return dialog;
    };

    const strings = (trigger) => {
      // Texts of the nearest section; if it has none (editorial rows, hotspots), use the ones from any section on the page, so the quick view is always translated
      const src = trigger.closest('[data-lc-qa-strings]') || document.querySelector('[data-lc-qa-strings]');
      const d = { preorder: 'Pre-order', instock: 'In stock:', ready: 'Ready to ship', preLabel: 'PRE-ORDER', estimated: 'Estimated shipping', add: 'Add to bag', added: 'Added to your bag', view: 'View bag', cont: 'Continue shopping', details: 'View full details', soldout: 'Sold out', color: 'Colour', qty: 'Quantity', error: 'Something went wrong. Please try again.' };
      if (src) { try { Object.assign(d, JSON.parse(src.dataset.lcQaStrings)); } catch (e) {} }
      return d;
    };

    const render = (product, t, trigger, ship) => {
      ship = ship || {};
      const dlg = getDialog();
      dlg.replaceChildren();

      const close = el('button', 'lc-qa__close');
      close.type = 'button';
      close.setAttribute('aria-label', 'Close');
      close.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';
      close.addEventListener('click', () => dlg.close());

      // Photo gallery: every product photo, with arrows, dots and swipe
      const media = el('div', 'lc-qa__media');
      const track = el('div', 'lc-qa__track');
      const fullUrl = (u) => (String(u).startsWith('//') ? 'https:' + u : String(u));
      const sameImg = (a, b) => String(a || '').split('?')[0].replace(/^https?:/, '') === String(b || '').split('?')[0].replace(/^https?:/, '');
      const photos = (product.images || []).filter(Boolean);
      if (!photos.length && product.featured_image) photos.push(product.featured_image);
      photos.forEach((u, n) => {
        const slide = el('div', 'lc-qa__slide');
        const im = el('img', 'lc-qa__img');
        im.alt = titleCase(product.title) + (photos.length > 1 ? ' (' + (n + 1) + '/' + photos.length + ')' : '');
        im.decoding = 'async';
        if (n > 0) im.loading = 'lazy';
        im.src = fullUrl(u) + (String(u).indexOf('?') > -1 ? '&' : '?') + 'width=1000';
        slide.appendChild(im);
        track.appendChild(slide);
      });
      media.appendChild(track);
      const img = track.querySelector('.lc-qa__img') || el('img', 'lc-qa__img');
      let current = 0;
      const goTo = (i, instant) => {
        if (!photos.length) return;
        current = Math.max(0, Math.min(photos.length - 1, i));
        track.scrollTo({ left: current * track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
      };
      if (photos.length > 1) {
        const mkArrow = (dir) => {
          const b = el('button', 'lc-qa__arrow lc-qa__arrow--' + dir);
          b.type = 'button';
          b.setAttribute('aria-label', dir === 'prev' ? 'Previous photo' : 'Next photo');
          b.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + (dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '"/></svg>';
          b.addEventListener('click', () => goTo(current + (dir === 'prev' ? -1 : 1)));
          return b;
        };
        const prev = mkArrow('prev');
        const next = mkArrow('next');
        const dots = el('div', 'lc-qa__dots');
        photos.forEach((_, n) => {
          const d = el('button', n === 0 ? 'is-on' : '');
          d.type = 'button';
          d.setAttribute('aria-label', 'Photo ' + (n + 1));
          d.addEventListener('click', () => goTo(n));
          dots.appendChild(d);
        });
        const count = el('span', 'lc-qa__count', '1 / ' + photos.length);
        const sync = () => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          current = i;
          dots.querySelectorAll('button').forEach((d, n) => d.classList.toggle('is-on', n === i));
          prev.disabled = i <= 0;
          next.disabled = i >= photos.length - 1;
          count.textContent = (i + 1) + ' / ' + photos.length;
        };
        track.addEventListener('scroll', () => { window.requestAnimationFrame(sync); }, { passive: true });
        media.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
        });
        media.append(prev, next, dots, count);
        prev.disabled = true;
      }

      const body = el('div', 'lc-qa__body');
      const title = el('h2', 'lc-qa__title', titleCase(product.title));
      title.id = 'lc-qa-title';
      const price = el('p', 'lc-qa__price');
      body.append(title, price);

      // Colour swatches: copied from the card (same look as the app), switch the popup in place
      const card = trigger && (trigger.closest('.lc-card') || (trigger.dataset.lcQaScope && document.getElementById(trigger.dataset.lcQaScope)));
      const cardSwatches = (card && card.querySelector('webyze-linkify-product-colors')) ||
        document.querySelector('[data-lc-swatch-product="' + product.id + '"] webyze-linkify-product-colors');
      if (cardSwatches) {
        const wrap = el('div', 'lc-qa__swatches lc-swatches');
        const legend = el('p', 'lc-qa__legend', t.color);
        const copy = cardSwatches.cloneNode(true);
        copy.querySelectorAll('linkify-product-colors-swatch').forEach((s) => s.toggleAttribute('data-selected', s.dataset.handle === product.handle));
        wrap.append(legend, copy);
        body.appendChild(wrap);
      }
      dlg._lc = { card, trigger, t, handle: product.handle };

      // Options as pills (hidden for single-variant products)
      const options = (product.options || []).filter((o) => !(product.variants.length === 1 && o.values.length === 1));
      const selected = (product.variants.find((v) => v.available) || product.variants[0]).options.slice();
      const groups = [];
      options.forEach((opt, i) => {
        const fs = el('fieldset', 'lc-qa__option');
        fs.appendChild(el('legend', 'lc-qa__legend', opt.name));
        const pills = el('div', 'lc-qa__pills');
        opt.values.forEach((val) => {
          const label = el('label', 'lc-qa__pill');
          const input = el('input');
          input.type = 'radio';
          input.name = 'lc-qa-opt-' + i;
          input.value = val;
          input.checked = selected[i] === val;
          input.addEventListener('change', () => { selected[i] = val; update(); });
          label.append(input, el('span', null, val));
          pills.appendChild(label);
        });
        fs.appendChild(pills);
        body.appendChild(fs);
        groups.push(pills);
      });

      // Shipping status, same rule as the product page
      const shipEl = el('p', 'lc-qa__ship');
      body.appendChild(shipEl);

      // Quantity + add
      const row = el('div', 'lc-qa__row');
      const qty = el('div', 'lc-qa__qty');
      const minus = el('button', 'lc-qa__step', '\u2212'); minus.type = 'button'; minus.setAttribute('aria-label', 'Decrease quantity');
      const input = el('input', 'lc-qa__qty-input'); input.type = 'number'; input.min = '1'; input.value = '1'; input.setAttribute('aria-label', t.qty);
      const plus = el('button', 'lc-qa__step', '+'); plus.type = 'button'; plus.setAttribute('aria-label', 'Increase quantity');
      minus.addEventListener('click', () => { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); });
      plus.addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) + 1; });
      qty.append(minus, input, plus);
      const add = el('button', 'lc-qa__add lc-btn', t.add); add.type = 'button';
      row.append(qty, add);
      body.appendChild(row);

      const msg = el('p', 'lc-qa__msg'); msg.setAttribute('role', 'status');
      body.appendChild(msg);

      const details = el('a', 'lc-qa__details lc-link', t.details);
      details.href = product.url || (root + 'products/' + product.handle);
      body.appendChild(details);

      let variant;
      function update() {
        variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i])) || product.variants[0];
        price.replaceChildren(document.createTextNode(money(variant.price)));
        if (variant.compare_at_price > variant.price) {
          const s = el('s', 'lc-qa__compare', money(variant.compare_at_price));
          price.appendChild(s);
        }
        const vsrc = variant.featured_image && variant.featured_image.src;
        if (vsrc) {
          const k = photos.findIndex((u) => sameImg(u, vsrc));
          if (k > -1) goTo(k, !dlg.open);
        }
        const info = ship[String(variant.id)];
        const isPre = !!(info && info.type === 'pre');
        add.disabled = !variant.available;
        add.textContent = !variant.available ? t.soldout : (isPre ? t.preorder : t.add);
        shipEl.replaceChildren();
        shipEl.className = 'lc-qa__ship' + (isPre ? ' lc-qa__ship--pre' : '');
        if (info && variant.available) {
          // Same wording and look as the product page
          shipEl.insertAdjacentHTML('afterbegin', '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 6.5h12v9.5H2zM14 9.5h4l3 3v3.5h-7"/><circle cx="6" cy="17.5" r="1.7"/><circle cx="17" cy="17.5" r="1.7"/></svg>');
          const txt = el('span');
          if (isPre) {
            txt.append(el('strong', null, t.preLabel));
            if (info.dates) txt.append(document.createTextNode(': ' + t.estimated + ' '), el('span', 'lc-qa__ship-date', info.dates));
          } else {
            txt.append(el('strong', null, t.instock), document.createTextNode(' ' + t.ready));
          }
          shipEl.appendChild(txt);
        }
        // mark values that have no available combination with the other choices
        groups.forEach((pills, i) => {
          pills.querySelectorAll('input').forEach((inp) => {
            const test = selected.slice(); test[i] = inp.value;
            const ok = product.variants.some((v) => v.available && v.options.every((o, k) => o === test[k]));
            inp.parentElement.classList.toggle('is-unavailable', !ok);
          });
        });
        details.href = (product.url || (root + 'products/' + product.handle)) + '?variant=' + variant.id;
      }
      update();

      add.addEventListener('click', async () => {
        add.disabled = true;
        msg.textContent = '';
        try {
          const res = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: Math.max(1, parseInt(input.value, 10) || 1) }] })
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || t.error);
          const cart = await (await fetch(root + 'cart.js')).json();
          // Tell the theme the cart changed (updates the bag counter in Horizon)
          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: 'lc-quick-add', data: { source: 'lc-quick-add', itemCount: cart.item_count } } }));
          showAdded();
        } catch (err) {
          msg.textContent = err.message || t.error;
          add.disabled = !variant.available;
        }
      });

      function showAdded() {
        body.replaceChildren();
        const done = el('div', 'lc-qa__done');
        done.innerHTML = '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M7.5 12.5l3 3 6-6"/></svg>';
        done.append(el('p', 'lc-qa__done-title', t.added), el('p', 'lc-qa__done-item', titleCase(product.title) + (variant.title && variant.title !== 'Default Title' ? ' \u2014 ' + variant.title : '')));
        const actions = el('div', 'lc-qa__actions');
        const view = el('a', 'lc-btn', t.view); view.href = root + 'cart';
        const cont = el('button', 'lc-qa__continue lc-link', t.cont); cont.type = 'button';
        cont.addEventListener('click', () => dlg.close());
        actions.append(view, cont);
        done.appendChild(actions);
        body.appendChild(done);
        view.focus();
      }

      dlg.append(close, media, body);
      if (!dlg.open) dlg.showModal();
      // always open on the first photo, centred, with the popup scrolled to the top
      dlg.scrollTop = 0;
      track.scrollLeft = 0;
      requestAnimationFrame(() => { track.scrollLeft = 0; dlg.scrollTop = 0; });
      const first = track.querySelector('.lc-qa__img');
      if (first && !first.complete) first.addEventListener('load', () => { if (current === 0) track.scrollLeft = 0; }, { once: true });
      if (current > 0) goTo(current, true);
      (body.querySelector('input:checked') || add).focus();
      dlg.addEventListener('close', () => { if (trigger && document.contains(trigger)) trigger.focus(); }, { once: true });
    };


    /* ---------- Colour swatches: switch the card in place instead of opening the product page ---------- */
    const productCache = {};
    const getProduct = (handle) => (productCache[handle] = productCache[handle] || fetch(root + 'products/' + handle + '.js').then((r) => (r.ok ? r.json() : Promise.reject())));
    const withWidth = (src, w) => src ? (src.startsWith('//') ? 'https:' + src : src) + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w : '';

    const swapCard = async (card, swatch) => {
      const handle = swatch.dataset.handle;
      if (!handle || swatch.hasAttribute('data-selected')) return;
      card.classList.add('is-swapping');
      try {
        const p = await getProduct(handle);
        const url = p.url || root + 'products/' + p.handle;
        const title = titleCase(p.title);
        // Replace the photo with a fresh <img> (more reliable than editing the old one,
        // which lazy-loading or other scripts may restore)
        const gal = card.querySelector('[data-lc-gallery]');
        const oldImg = card.querySelector('.lc-card__img');
        const src = p.featured_image || (p.images && p.images[0]) || (p.media && p.media[0] && p.media[0].src);
        const imgs = (p.images || []).filter(Boolean);
        if (gal && imgs.length && oldImg) {
          // rebuild the gallery with the new colour's photos
          const max = Number(gal.dataset.lcGallery) || 4;
          const list = imgs.slice(0, max);
          const track = gal.querySelector('.lc-gal__track');
          const media = gal.closest('.lc-card__media');
          const sizes = oldImg.getAttribute('sizes') || '(min-width: 990px) 22vw, 50vw';
          track.innerHTML = '';
          list.forEach((u, n) => {
            const slide = document.createElement('div');
            slide.className = 'lc-gal__slide';
            const img = document.createElement('img');
            img.className = 'lc-card__img';
            img.alt = p.title;
            img.width = oldImg.width || 900;
            img.height = oldImg.height || 900;
            img.decoding = 'async';
            if (n > 0) img.loading = 'lazy';
            img.sizes = sizes;
            img.srcset = [300, 450, 600, 900, 1200].map((w) => withWidth(u, w) + ' ' + w + 'w').join(', ');
            img.src = withWidth(u, 900);
            slide.appendChild(img);
            track.appendChild(slide);
          });
          track.scrollLeft = 0;
          const dots = media && media.querySelector('.lc-gal__dots');
          if (dots) dots.innerHTML = list.map((_, n) => '<span' + (n === 0 ? ' class="is-on"' : '') + '></span>').join('');
          media && media.querySelectorAll('.lc-gal__arrow, .lc-gal__dots').forEach((x) => { x.hidden = list.length < 2; });
          lcGalUpdate(track);
        } else if (oldImg && src) {
          const hov = card.querySelector('.lc-card__hover');
          if (hov) {
            const u2 = imgs[1];
            if (u2) { hov.srcset = [300, 450, 600, 900].map((w) => withWidth(u2, w) + ' ' + w + 'w').join(', '); hov.src = withWidth(u2, 900); }
            else hov.remove();
          }
          const img = document.createElement('img');
          img.className = oldImg.className;
          img.alt = p.title;
          img.width = oldImg.width || 900;
          img.height = oldImg.height || 900;
          img.decoding = 'async';
          img.sizes = oldImg.getAttribute('sizes') || '(min-width: 990px) 22vw, 50vw';
          img.srcset = [300, 450, 600, 900, 1200].map((w) => withWidth(src, w) + ' ' + w + 'w').join(', ');
          img.src = withWidth(src, 900);
          oldImg.replaceWith(img);
        }
        card.querySelectorAll('.lc-card__img-link, .lc-card__title').forEach((a) => { a.href = url; });
        const t = card.querySelector('.lc-card__title');
        if (t) t.textContent = title;
        const plus = card.querySelector('[data-lc-quick-add]');
        if (plus) { plus.dataset.lcQuickAdd = p.handle; plus.href = url; plus.setAttribute('aria-label', 'Quick add ' + title); }
        const price = card.querySelector('.lc-card__price');
        if (price) {
          price.textContent = p.price_min !== p.price_max ? money(p.price_min) + ' – ' + money(p.price_max) : money(p.price);
          if (p.compare_at_price > p.price) price.appendChild(el('s', 'lc-card__compare', money(p.compare_at_price)));
        }
        swatch.parentElement.querySelectorAll('linkify-product-colors-swatch').forEach((s) => s.toggleAttribute('data-selected', s === swatch));
        if (typeof lcEqualizeTitles === 'function') lcEqualizeTitles();
      } catch (err) {
        window.location.href = root + 'products/' + handle; // fall back to the normal behaviour
      } finally {
        card.classList.remove('is-swapping');
      }
    };

    const swatchTarget = (e) => {
      const swatch = e.target.closest && e.target.closest('linkify-product-colors-swatch[data-handle]');
      const card = swatch && swatch.closest('.lc-card');
      return swatch && card ? { swatch, card } : null;
    };
    // Capture phase: runs before the app's own click handler, which would open the product page
    const popupSwatch = async (swatch) => {
      const dlg = swatch.closest('.lc-qa');
      const ctx = dlg && dlg._lc;
      const handle = swatch.dataset.handle;
      if (!ctx || !handle || handle === ctx.handle) return;
      dlg.classList.add('is-swapping');
      try {
        const p = await getProduct(handle);
        // keep the card behind in sync with the colour chosen in the popup
        const cardSwatch = ctx.card && ctx.card.querySelector('linkify-product-colors-swatch[data-handle="' + handle + '"]');
        if (cardSwatch) await swapCard(ctx.card, cardSwatch);
        render(p, ctx.t, ctx.trigger, await getShip(handle));
      } catch (err) {
        window.location.href = root + 'products/' + handle;
      } finally {
        dlg.classList.remove('is-swapping');
      }
    };
    const popupTarget = (e) => {
      const swatch = e.target.closest && e.target.closest('.lc-qa linkify-product-colors-swatch[data-handle]');
      return swatch || null;
    };
    document.addEventListener('click', (e) => {
      const ps = popupTarget(e);
      if (ps) { e.preventDefault(); e.stopPropagation(); popupSwatch(ps); return; }
      const hit = swatchTarget(e);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      swapCard(hit.card, hit.swatch);
    }, true);
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const ps = popupTarget(e);
      if (ps) { e.preventDefault(); e.stopPropagation(); popupSwatch(ps); return; }
      const hit = swatchTarget(e);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      swapCard(hit.card, hit.swatch);
    }, true);
    // Preload on hover so the switch feels instant
    document.addEventListener('pointerover', (e) => {
      const sw = e.target.closest && e.target.closest('linkify-product-colors-swatch[data-handle]');
      if (sw && (sw.closest('.lc-card') || sw.closest('.lc-qa'))) getProduct(sw.dataset.handle).catch(() => {});
    });

    document.addEventListener('click', async (e) => {
      const trigger = e.target.closest('[data-lc-quick-add]');
      if (!trigger) return;
      e.preventDefault();
      trigger.classList.add('is-loading');
      try {
        const handle = trigger.dataset.lcQuickAdd;
        const [product, ship] = await Promise.all([getProduct(handle), getShip(handle)]);
        render(product, strings(trigger), trigger, ship);
      } catch (err) {
        window.location.href = trigger.href; // fall back to the product page
      } finally {
        trigger.classList.remove('is-loading');
      }
    });
  }
  window.lcCards = { queueSwatches: lcQueueAnchors, equalize: lcEqualizeTitles, fades: lcSwatchFades };
})();